/**
 * Framework-free cookie-consent model shared by the client instrumentation
 * hook (runs before React) and React components.
 */
import { logger } from '@lib/logger'

export const CONSENT_COOKIE = 'cookie_consent'

// Bump to invalidate previously stored choices and re-prompt every visitor
// (e.g. when a new cookie category is introduced).
export const CONSENT_VERSION = 1

// Re-prompt after 180 days.
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180

export interface ConsentState {
  v: number
  /** Essential cookies cannot be refused — always true. */
  essential: true
  /** Analytics/tracking (PostHog, Vercel Analytics, session replay). */
  analytics: boolean
  /** ISO timestamp of when the choice was made. */
  ts: string
  /** Set when a Global Privacy Control signal was present at decision time. */
  gpc?: boolean
}

export function parseConsentCookie(raw: string | null | undefined): ConsentState | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState>
    if (parsed.v !== CONSENT_VERSION) return null
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.ts !== 'string') return null
    return {
      v: CONSENT_VERSION,
      essential: true,
      analytics: parsed.analytics,
      ts: parsed.ts,
      ...(parsed.gpc === true ? { gpc: true } : {}),
    }
  } catch (err) {
    logger.error('consent', 'malformed consent cookie', err)
    return null
  }
}

export function serializeConsent(state: ConsentState): string {
  return JSON.stringify(state)
}

/** Extract and parse the consent cookie from a `document.cookie`-style string. */
export function consentFromCookieString(cookieString: string | null | undefined): ConsentState | null {
  if (!cookieString) return null
  for (const part of cookieString.split('; ')) {
    if (part.startsWith(`${CONSENT_COOKIE}=`)) {
      let value = part.slice(CONSENT_COOKIE.length + 1)
      try {
        value = decodeURIComponent(value)
      } catch (err) {
        // value was not URI-encoded; use as-is
        logger.error('consent', 'consent cookie value was not URI-encoded', err)
      }
      return parseConsentCookie(value)
    }
  }
  return null
}
